import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { STORAGE_KEYS, API_BASE_URL } from '../constants';
import { authService, RegisterRequest } from '../services/authService';
import { attributionForRegisterBody, syncAttributionToBackend } from '../lib/attribution';
import { apiService } from '../services/api';

export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  fullName?: string;
  phone?: string;
  nationality?: string;
  dateOfBirth?: string;
  companyName?: string;
  role: 'general' | 'corporate' | 'admin' | 'enterprise' | 'agent';
  isEmailVerified: boolean;
  createdAt: string;
  updatedAt: string;
}

interface AuthResult {
  success: boolean;
  message?: string;
  user?: User;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string, turnstileToken?: string) => Promise<AuthResult>;
  register: (userData: RegisterData) => Promise<AuthResult>;
  logout: () => void;
  updateProfile: (data: Partial<User>) => Promise<{ success: boolean; message?: string }>;
  refreshToken: () => Promise<boolean>;
  setUser: (user: User | null) => void; // For testing role switching
  switchRole: (newRole: string) => Promise<void>; // Add missing switchRole function
}

interface RegisterData {
  email: string;
  password: string;
  fullName: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  nationality?: string;
  dateOfBirth?: string;
  role?: string;
  agreeToTerms?: boolean;
  turnstileToken?: string;
}

interface AuthProviderProps {
  children: ReactNode;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const isDemoEnabled = process.env.REACT_APP_ENABLE_DEMO === 'true'
    || process.env.REACT_APP_ENVIRONMENT !== 'production';

  const isAuthenticated = !!user && !!token;

  const clearAuthData = useCallback(() => {
    setUser(null);
    setToken(null);
    localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.USER_DATA);
    authService.logout();
  }, []);

  const validateToken = useCallback(async (tokenToValidate: string) => {
    try {
      // Skip validation for demo tokens only when demo is enabled
      if (isDemoEnabled && tokenToValidate && tokenToValidate.startsWith('demo-token-')) {
        return true; // Demo tokens are always valid
      }
      
      const isValid = await authService.validateToken();
      if (!isValid) {
        clearAuthData();
        return false;
      }
      return true;
    } catch (error) {
      console.error('Token validation failed:', error);
      // Don't clear auth data if it's a demo token (demo mode only)
      if (!isDemoEnabled || !tokenToValidate || !tokenToValidate.startsWith('demo-token-')) {
        clearAuthData();
      }
      return !!(isDemoEnabled && tokenToValidate && tokenToValidate.startsWith('demo-token-'));
    }
  }, [clearAuthData, isDemoEnabled]);

  // Initialize auth state from localStorage and validate with backend
  useEffect(() => {
    const initializeAuth = async () => {
      console.log('AuthContext: Initializing authentication...');

      // ── Platform Hub SSO (one-time exchange code) ──
      const params = new URLSearchParams(window.location.search);
      const ssoCode = params.get('sso_code');
      if (ssoCode) {
        console.log('AuthContext: Platform Hub SSO code detected — exchanging');
        try {
          const resp = await fetch(`${API_BASE_URL}/v1/sso/exchange`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ code: ssoCode }),
          });
          const data = await resp.json();
          if (resp.ok && data?.success && data?.data?.token) {
            const u = data.data.user;
            const ssoUser: User = {
              id: u.id,
              email: u.email,
              firstName: u.firstName || 'User',
              lastName: u.lastName || '',
              fullName: u.name || `${u.firstName || ''} ${u.lastName || ''}`.trim(),
              role: (u.role || 'general') as User['role'],
              isEmailVerified: true,
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
            };
            localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, data.data.token);
            localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(ssoUser));
            apiService.setAuthToken(data.data.token);
            setToken(data.data.token);
            setUser(ssoUser);
            setIsLoading(false);
            const redirectTo =
              params.get('returnPath') || params.get('redirect') || '/dashboard';
            params.delete('sso_code');
            params.delete('sso');
            params.delete('redirect');
            params.delete('returnPath');
            params.delete('error');
            window.history.replaceState({}, '', window.location.pathname);
            window.location.replace(redirectTo.startsWith('/') ? redirectTo : '/dashboard');
            return;
          }
          console.error('AuthContext: SSO exchange failed', data);
        } catch (err) {
          console.error('AuthContext: SSO exchange error', err);
        }
      }

      // ── Cognito SSO callback handling ──
      // If the URL contains SSO params (from Cognito redirect), process them first
      const ssoAccessToken = params.get('accessToken');
      if (ssoAccessToken) {
        console.log('AuthContext: SSO callback detected — processing tokens');
        const email = params.get('email') || '';
        const firstName = params.get('firstName') || 'User';
        const lastName = params.get('lastName') || '';
        const ssoRole = params.get('role') || 'general';
        let jwtUserId = '';
        try {
          const part = ssoAccessToken.split('.')[1] || '';
          const payload = JSON.parse(atob(part.replace(/-/g, '+').replace(/_/g, '/')));
          jwtUserId = String(payload.userId || payload.sub || '');
        } catch {
          jwtUserId = '';
        }

        const ssoUser: User = {
          id: jwtUserId || email || Date.now().toString(),
          email,
          firstName,
          lastName,
          fullName: `${firstName} ${lastName}`.trim(),
          role: ssoRole as User['role'],
          isEmailVerified: true,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };

        // Persist to localStorage
        localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, ssoAccessToken);
        localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(ssoUser));

        // Set auth token for API calls
        apiService.setAuthToken(ssoAccessToken);

        // Set React state
        setToken(ssoAccessToken);
        setUser(ssoUser);
        setIsLoading(false);

        // Clean SSO params from URL without reloading
        params.delete('accessToken');
        params.delete('refreshToken');
        params.delete('accessTokenRaw');
        params.delete('email');
        params.delete('firstName');
        params.delete('lastName');
        params.delete('role');
        params.delete('sso');
        params.delete('error');
        params.delete('reason');
        const cleanPath = window.location.pathname + (params.toString() ? `?${params.toString()}` : '');
        window.history.replaceState({}, '', cleanPath);

        console.log('AuthContext: SSO login complete for', email, '— state set, letting LoginPage route to dashboard');
        // Do NOT force a full page reload here. The LoginPage effect watches
        // isAuthenticated and navigates to /dashboard via React Router. A full
        // reload re-runs initializeAuth, validates the token against /api/auth/profile,
        // and if that request fails (network, CORS, 401 race) the user gets kicked
        // back to /login. Keeping it in the same SPA session is safer.
        return;
      }

      // Check for SSO error
      const ssoError = params.get('error');
      if (ssoError) {
        console.error('AuthContext: SSO error received:', ssoError);
        params.delete('error');
        const cleanPath = window.location.pathname + (params.toString() ? `?${params.toString()}` : '');
        window.history.replaceState({}, '', cleanPath);
      }

      const savedToken = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
      const savedUser = localStorage.getItem(STORAGE_KEYS.USER_DATA);

      if (savedToken && savedUser) {
        try {
          const parsedUser = JSON.parse(savedUser);
          console.log('AuthContext: Found saved auth data', { token: savedToken, user: parsedUser });
          
          // For demo tokens, immediately set auth state without backend validation (demo only)
          if (isDemoEnabled && savedToken.startsWith('demo-token-')) {
            console.log('AuthContext: Setting demo authentication');
            apiService.setAuthToken(savedToken);
            setToken(savedToken);
            setUser(parsedUser);
            setIsLoading(false);
            return;
          }
          
          // For real tokens, validate with backend first
          console.log('AuthContext: Validating real token with backend');
          authService.initializeAuth();
          const isValid = await validateToken(savedToken);
          
          if (isValid) {
            console.log('AuthContext: Token validation successful');
            apiService.setAuthToken(savedToken);
            setToken(savedToken);
            setUser(parsedUser);
          } else {
            console.log('AuthContext: Token validation failed');
          }
        } catch (error) {
          console.error('Error parsing saved user data:', error);
          clearAuthData();
        }
      } else {
        console.log('AuthContext: No saved auth data found');
      }
      setIsLoading(false);
      console.log('AuthContext: Initialization complete');
    };

    initializeAuth();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const login = async (email: string, password: string, turnstileToken?: string): Promise<AuthResult> => {
    try {
      setIsLoading(true);
      
      // Try backend login first
      try {
        const response = await authService.login({ email, password, turnstileToken });

        if (response.success && response.data) {
          const { token: authToken, user: userData } = response.data;
          const verified = Boolean(
            (userData as any).isEmailVerified ?? (userData as any).emailVerified
          );
          
          const fullUser = {
            ...userData,
            fullName: `${userData.firstName} ${userData.lastName}`,
            role: userData.role as any,
            isEmailVerified: verified,
          };
          
          setToken(authToken);
          setUser(fullUser);
          
          // Persist to localStorage with correct keys
          localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, authToken);
          localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(fullUser));
          
          // Set API service auth token for subsequent requests
          apiService.setAuthToken(authToken);
          syncAttributionToBackend(authToken).catch(() => {});
          
          return { success: true, message: 'Login successful!', user: fullUser };
        } else {
          throw new Error(response.message || 'Login failed');
        }
      } catch (backendError) {
        if (!isDemoEnabled) {
          return { success: false, message: 'Login failed. Please try again.' };
        }

        console.log('Backend unavailable, using demo login');
        
        // Demo user database with real credentials
        const demoUsers = {
          'admin@tripnovac.com': { password: 'admin2024', role: 'admin', firstName: 'Admin', lastName: 'User' },
          'agent@tripnovac.com': { password: 'agent2024', role: 'agent', firstName: 'Agent', lastName: 'Smith' },
          'corporate@tripnovac.com': { password: 'corporate2024', role: 'corporate', firstName: 'Corporate', lastName: 'Manager' },
          'enterprise@tripnovac.com': { password: 'enterprise2024', role: 'enterprise', firstName: 'Enterprise', lastName: 'Director' },
          'general@tripnovac.com': { password: 'general2024', role: 'general', firstName: 'John', lastName: 'Doe' }
        };
        
        const demoUser = demoUsers[email as keyof typeof demoUsers];
        
        if (!demoUser || demoUser.password !== password) {
          return { success: false, message: 'Invalid email or password' };
        }
        
        // Create authenticated user
        const authenticatedUser: User = {
          id: Date.now().toString(),
          email: email,
          firstName: demoUser.firstName,
          lastName: demoUser.lastName,
          fullName: `${demoUser.firstName} ${demoUser.lastName}`,
          role: demoUser.role as any,
          isEmailVerified: true,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };
        
        const demoToken = 'demo-token-' + Date.now();
        
        console.log('AuthContext: Setting demo user authentication', { user: authenticatedUser, token: demoToken });
        setToken(demoToken);
        setUser(authenticatedUser);
        
        // Store in localStorage
        localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, demoToken);
        localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(authenticatedUser));
        
        console.log('AuthContext: Demo login successful', { user: authenticatedUser });
        return { success: true, message: 'Login successful!' };
      }
    } catch (error: any) {
      console.error('Login error:', error);
      return { 
        success: false, 
        message: error.message || 'Network error. Please try again.' 
      };
    } finally {
      setIsLoading(false);
    }
  };

  const register = async (userData: RegisterData): Promise<AuthResult> => {
    try {
      setIsLoading(true);
      
      // Try backend register first
      try {
        // Transform RegisterData to RegisterRequest format
        const [firstName, ...lastNameParts] = userData.fullName.split(' ');
        const lastName = lastNameParts.join(' ') || firstName;
        
        const registerRequest: RegisterRequest = {
          email: userData.email,
          password: userData.password,
          firstName,
          lastName,
          role: userData.role,
          phone: userData.phone,
          nationality: userData.nationality,
          dateOfBirth: userData.dateOfBirth,
          acceptedTerms: userData.agreeToTerms === true,
          agreeToTerms: userData.agreeToTerms === true,
          turnstileToken: userData.turnstileToken,
          website: '',
          ...attributionForRegisterBody(),
        };
        
        const response = await authService.register(registerRequest);

        if (response.success && response.data) {
          const { token: authToken, user: userInfo } = response.data;
          const verified = Boolean(
            (userInfo as any).isEmailVerified ?? (userInfo as any).emailVerified
          );
          
          const fullUser = {
            ...userInfo,
            fullName: `${userInfo.firstName} ${userInfo.lastName}`,
            role: userInfo.role as any,
            isEmailVerified: verified,
          };
          
          setToken(authToken);
          setUser(fullUser);
          
          localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, authToken);
          localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(fullUser));
          apiService.setAuthToken(authToken);
          syncAttributionToBackend(authToken).catch(() => {});
          
          return { 
            success: true, 
            message: verified
              ? 'Registration successful!'
              : 'Account created. Please verify your email to continue.',
            user: fullUser
          };
        } else {
          throw new Error(response.message || 'Registration failed');
        }
      } catch (backendError: any) {
        // Real backend error - no fallback to demo
        console.error('Registration backend error:', backendError);
        return { 
          success: false, 
          message: backendError.message || 'Registration failed. Please try again.' 
        };
      }
    } catch (error: any) {
      console.error('Registration error:', error);
      return { 
        success: false, 
        message: error.message || 'Network error. Please try again.' 
      };
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    const t = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
    clearAuthData();
    // Backend logout + Hub-compatible local JWT revoke
    authService.logout().catch(console.error);
    if (t) {
      fetch(`${API_BASE_URL}/v1/sso/revoke`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${t}` },
        body: JSON.stringify({ token: t }),
      }).catch(() => {});
    }
  };

  // Custom setUser function for role switching and user management
  const setUserWithPersistence = useCallback((newUser: User | null) => {
    setUser(newUser);
    if (newUser) {
      localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(newUser));
    } else {
      localStorage.removeItem(STORAGE_KEYS.USER_DATA);
    }
  }, []);

  const updateProfile = async (data: Partial<User>): Promise<{ success: boolean; message?: string }> => {
    try {
      setIsLoading(true);
      
      const response = await authService.updateProfile(data);

      if (response.success && response.data) {
        const updatedUser = response.data;
        setUser({
          ...updatedUser,
          fullName: `${updatedUser.firstName} ${updatedUser.lastName}`,
          role: updatedUser.role as any
        });
        localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(updatedUser));
        
        return { success: true, message: 'Profile updated successfully!' };
      } else {
        return { success: false, message: response.message || 'Profile update failed' };
      }
    } catch (error: any) {
      console.error('Profile update error:', error);
      return { 
        success: false, 
        message: error.message || 'Network error. Please try again.' 
      };
    } finally {
      setIsLoading(false);
    }
  };

  const refreshToken = async (): Promise<boolean> => {
    try {
      if (!token) return false;
      
      // Demo tokens never expire
      if (token.startsWith('demo-token-')) {
        return true;
      }
      
      // For now, validate existing token instead of refresh
      // This can be implemented when refresh endpoint is available
      const isValid = await authService.validateToken();
      return isValid;
    } catch (error) {
      console.error('Token refresh failed:', error);
      return false;
    }
  };

  // Switch role function for enterprise role-based dashboard system
  const switchRole = async (newRole: string): Promise<void> => {
    try {
      if (!user) return;
      
      // Update user role locally (in production, this would require backend validation)
      const updatedUser = {
        ...user,
        role: newRole as 'general' | 'corporate' | 'admin' | 'enterprise' | 'agent'
      };
      
      setUserWithPersistence(updatedUser);
      
      // In production, this would call the backend to validate role change
      console.log(`Role switched to: ${newRole}`);
    } catch (error) {
      console.error('Role switch failed:', error);
      throw error;
    }
  };

  const value: AuthContextType = {
    user,
    token,
    isLoading,
    isAuthenticated,
    login,
    register,
    logout,
    updateProfile,
    refreshToken,
    setUser: setUserWithPersistence,
    switchRole,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
